/**
 * Images put in the text (part 16b, table I): a paste or drop that holds an image file hands its files to the app
 * (which imports them through part 16a's pipeline) instead of putting text in, and marks keep the places the images
 * will go while they import, following the writer's edits around them. The editor writes nothing until the app gives it
 * the image's markup for a mark.
 */
import { Prec, StateEffect, StateField } from '@codemirror/state';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { parseChapter, visibleBlocks } from '@cockatrice/core';
import type { ParsedChapter } from '@cockatrice/core';
import type { Tree } from '@lezer/common';
import { MAX_HTML_LENGTH, richSource } from './rich-paste.ts';
import { leafAt, lineContent } from './structure.ts';

/** I2, I3: what the editor hands the app: the files of a paste or drop, and where in the text they go. */
export type ImageFilesHandler = (files: readonly File[], at: number) => void;

/** Deep-fix r4, J2: a word is a letter, digit, punctuation mark or symbol; blanks and no-break spaces are none. */
const WORD = /[\p{L}\p{N}\p{P}\p{S}]/u;

/**
 * Table J (deep-fix r1, r2, r4, the user's choice: words decide): whether HTML beside a picture holds words, which the
 * writer copied as text: always past rich paste's limit (part 10 pastes it as plain text, H8); else whether what rich
 * paste would put in shows a word. Empty paragraphs (Word's `<o:p>&nbsp;</o:p>`), line breaks and a lone `<img>` don't.
 */
function holdsWords(html: string): boolean {
  if (html.length > MAX_HTML_LENGTH) return true;
  const source = richSource(html);
  return source !== null && visibleBlocks(parseChapter(source)).some((block) => WORD.test(block.text));
}

/**
 * I2–I4, table J: every file of a paste or drop that holds at least one image file (`image/*`) and no HTML with words;
 * null otherwise. Word, LibreOffice and a web page's selection put a picture of the text beside its HTML: the text is
 * what the writer copied, so it goes the text's way (part 10's paste). HTML with no words (a copied web image's `<img>`,
 * a picture with empty paragraphs), or none (a screenshot, a file manager's path or name as plain text), leaves the files
 * to be pictures.
 */
export function imageFiles(data: DataTransfer | null): File[] | null {
  if (data === null) return null;
  const files = Array.from(data.files);
  if (!files.some((file) => file.type.startsWith('image/'))) return null;
  const html = data.getData('text/html');
  return html === '' || !holdsWords(html) ? files : null;
}

/** K2–K5: whether a position strictly inside a node named `name` (whose link marks start at `marks`) holds no image. */
function closed(name: string, pos: number, marks: readonly number[]): boolean {
  if (name === 'Image' || name === 'FootnoteReference' || name === 'Escape' || name === 'Entity') return true;
  // K3: a link's text holds images (the layout draws them); its `](destination "title")` doesn't. The second mark is `]`.
  const close = marks[1];
  return name === 'Link' && close !== undefined && pos > close;
}

/**
 * Deep-fix r2, table K: a place for an image clear of inline constructs: `pos` itself between text, in a span's or a
 * link's text (K1); after the image, link destination, note reference, escape or entity `pos` is strictly inside
 * (K2–K5), again until it is inside none. Part 10's filter then moves a place inside a backslash break to its start (K6).
 */
function inlinePoint(tree: Tree, pos: number): number {
  let point = pos;
  for (let moved = true; moved; ) {
    moved = false;
    for (let node = tree.resolveInner(point, 1); node.parent !== null; node = node.parent) {
      const marks = node.getChildren('LinkMark').map((mark) => mark.from);
      if (point > node.from && point < node.to && closed(node.name, point, marks)) {
        point = node.to;
        moved = true;
        break;
      }
    }
  }
  return point;
}

/**
 * A place in the text kept across edits (I9): `assoc` −1 stays before text put in at it (typing where the image will
 * go stays after the image), 1 moves after it (while an image goes in at it, so the next image of the batch follows).
 */
interface Mark {
  readonly pos: number;
  readonly assoc: -1 | 1;
}

// Dispatched alone, with no changes, so never mapped.
const setMark = StateEffect.define<{ readonly id: number; readonly mark: Mark }>();
const holdMark = StateEffect.define<number>();
const dropMark = StateEffect.define<number>();

/** I9, I10: the marks, by ID, mapped through every change (a deleted range takes a mark inside it to its edge). */
export const imageMarks: StateField<ReadonlyMap<number, Mark>> = StateField.define<ReadonlyMap<number, Mark>>({
  create: () => new Map(),
  update(marks, transaction) {
    let next = marks;
    if (transaction.docChanged && marks.size > 0) {
      next = new Map([...marks].map(([id, mark]) => [id, { ...mark, pos: transaction.changes.mapPos(mark.pos, mark.assoc) }]));
    }
    for (const effect of transaction.effects) {
      if (effect.is(setMark)) next = new Map(next).set(effect.value.id, effect.value.mark);
      else if (effect.is(holdMark)) next = new Map([...next].map(([id, mark]) => [id, id === effect.value ? { ...mark, assoc: -1 as const } : mark]));
      else if (effect.is(dropMark)) {
        const without = new Map(next);
        without.delete(effect.value);
        next = without;
      }
    }
    return next;
  },
});

/** The effect that places mark `id` at `pos` (I9: before text later put in at it). */
export function placeMark(id: number, pos: number, assoc: -1 | 1 = -1): StateEffect<unknown> {
  return setMark.of({ id, mark: { pos, assoc } });
}

/** The effect that turns mark `id` back to staying before text put in at it (I9), where it stands. */
export function holdImageMark(id: number): StateEffect<unknown> {
  return holdMark.of(id);
}

/** The effect that forgets mark `id`. */
export function forgetMark(id: number): StateEffect<unknown> {
  return dropMark.of(id);
}

/**
 * I2, I3, I12: a paste or drop holding an image file goes to `onImages`, at the selection's start or the drop point,
 * and nothing else of it is put in; read-only, it is taken and dropped (a dropped file must never open in the tab). A
 * paste or drop with no image file takes the editor's own paths (I4).
 */
export function imageInput(onImages: ImageFilesHandler): Extension {
  const take = (view: EditorView, event: Event, files: File[] | null, at: () => number | null): boolean => {
    if (files === null) return false;
    event.preventDefault();
    const place = at();
    if (!view.state.readOnly && place !== null) onImages(files, place);
    return true;
  };
  return Prec.high(
    EditorView.domEventHandlers({
      paste: (event, view) => take(view, event, imageFiles(event.clipboardData), () => view.state.selection.main.from),
      drop: (event, view) => take(view, event, imageFiles(event.dataTransfer), () => view.posAtCoords({ x: event.clientX, y: event.clientY })),
    }),
  );
}

/**
 * Deep-fix r3, table B: a place for an image clear of block markup, on the line of `pos`: among a continuation line's
 * container marks (a quote's `>`, an item's indent), that line's content start (B0, part 10's Q1); in a leaf's content,
 * ends included, itself (B1); before the content on its line (a heading's `#`, a quote's `>`, an item's mark, a note's
 * `[^label]:`, a centred block's `->`, indent), that content's start (B2); after it (a closing `<-` or `#`s, blanks), its
 * end (B3); on a setext heading's underline, the heading's content end (B4); on a line with no content (a scene break, an
 * empty item or quote, a blank line), the line's end, where part 10's filter starts a paragraph (B5).
 */
function blockPoint(parsed: ParsedChapter, at: number): number {
  const pos = lineContent(parsed, at);
  if (leafAt(parsed, pos) !== null) return pos;
  const { text } = parsed;
  const lineStart = text.lastIndexOf('\n', pos - 1) + 1;
  const end = text.indexOf('\n', pos);
  const lineEnd = end < 0 ? text.length : end;
  for (let p = pos; p <= lineEnd; p += 1) if (leafAt(parsed, p) !== null) return p;
  for (let p = pos; p >= lineStart; p -= 1) {
    const leaf = leafAt(parsed, p);
    if (leaf !== null) return leaf.contentTo;
  }
  const above = lineStart > 0 ? leafAt(parsed, lineStart - 1) : null;
  return above !== null && above.node.to > lineStart ? above.contentTo : lineEnd;
}

/** Tables K and B: where an image put in at `pos` goes, clear of every construct but the text it joins. */
export function imagePoint(parsed: ParsedChapter, pos: number): number {
  return blockPoint(parsed, inlinePoint(parsed.tree, pos));
}
